import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";
import { match } from "ts-pattern";

import { StarIcon } from "src/assets/icons/star_icon";
import ExpertiseAndResourcesImg from "src/assets/images/domainexpertise.png";
import LearningOrientationImg from "src/assets/images/domainlearning.png";
import PurposeImg from "src/assets/images/domainpurpose.png";
import RelationshipsImg from "src/assets/images/domainrelationships.png";
import WellBeingImg from "src/assets/images/domainwellbeing.png";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FSCIBrandedV1Report } from "src/pages/reports/fsci/v1/types";
import { isConstructHidden } from "src/config/hiddenConstructs";

import { meta } from "./meta";

/*
 * Types
 */

export type MeaningfulStrengthsProps = {
    readonly report: FSCIBrandedV1Report;
};

/*
 * Main
 */

export const MeaningfulStrengths = (props: MeaningfulStrengthsProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <PageTitle className="gold-icon">
                <StarIcon />
                Meaningful Strengths
            </PageTitle>
            <Body>
                Below are five strengths of your school in regard to creating and sustaining a flourishing school
                culture. These areas emerged from your school’s overall FSCI profile (meaning these areas are{" "}
                <em>not</em> benchmarked against other Christian schools, but are the top five strengths that emerged
                from your school’s data and are specific to your school’s unique profile). Your school’s meaningful
                strengths are areas to affirm, share, and celebrate with your school community as well as external
                audiences.
            </Body>
            <Separator />
            <div className="sub-item-wrap">
                {pipe(
                    report.value.results.meaningfulStrengths,
                    A.filter(({ construct }) => !isConstructHidden(construct)),
                    A.map(({ domain, construct }) => {
                        return (
                            <div className="an-opportunity-item s-and-g-opp" key={construct}>
                                <img
                                    alt={`${domain} key growth opportunity domain`}
                                    className="domain-image"
                                    src={match(domain)
                                        .with("expertiseAndResources", () => ExpertiseAndResourcesImg)
                                        .with("learningOrientation", () => LearningOrientationImg)
                                        .with("purpose", () => PurposeImg)
                                        .with("relationships", () => RelationshipsImg)
                                        .with("wellBeing", () => WellBeingImg)
                                        .exhaustive()}
                                />
                                {pipe(
                                    R.lookup(domain, meta),
                                    O.chain(R.lookup(construct)),
                                    O.map((m) => {
                                        return (
                                            <>
                                                <SectionTitle>{m.title}</SectionTitle>
                                                <Body>{m.prologue}</Body>
                                            </>
                                        );
                                    }),
                                    O.getOrElse(() => {
                                        return (
                                            <>
                                                <SectionTitle>Unknown</SectionTitle>
                                                <Body>Unknown</Body>
                                            </>
                                        );
                                    }),
                                )}
                            </div>
                        );
                    }),
                )}
            </div>
        </div>
    );
};
